// ============================================================
// PAYMENT PLAN DETAIL PAGE
// ============================================================

import { AlertCircle, ArrowLeft, CheckCircle2, Clock, CreditCard, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MarkAsPaidModal } from '../../components/quotes/MarkAsPaidModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { api } from '../../services/api';
import type { PaymentPlan } from '../../types/payment.types';
import { extractSpaceNameFromPriceBreakdown } from '../../utils/extractSpaceName';

export function PaymentPlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validating, setValidating] = useState<string | null>(null);
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState<any>(null);

  useEffect(() => {
    fetchPaymentPlan();
  }, [id]);

  const fetchPaymentPlan = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/payment-plans/${id}`);

      if (data.success) {
        setPaymentPlan(data.data);
      }
    } catch (err) {
      setError('Erreur lors du chargement du plan de paiement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenValidationModal = (installment: any) => {
    setSelectedInstallment(installment);
    setShowMarkAsPaidModal(true);
  };

  const handleValidateInstallment = async (data: { method: string; date: string; proofDocumentUrl: string }) => {
    if (!selectedInstallment) return;

    try {
      setValidating(selectedInstallment.id);
      await api.post(`/payment-plans/installment/${selectedInstallment.id}/validate`, {
        paymentMethod: data.method,
        paymentDate: data.date,
        proofDocumentUrl: data.proofDocumentUrl,
      });

      toast.success('Paiement validé avec succès');
      setShowMarkAsPaidModal(false);
      setSelectedInstallment(null);

      // Recharger les données
      await fetchPaymentPlan();
    } catch (err: any) {
      console.error('Erreur validation:', err);
      toast.error(err.response?.data?.error || 'Erreur lors de la validation du paiement');
    } finally {
      setValidating(null);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Chargement du plan de paiement" />;
  }

  if (error || !paymentPlan) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <span>{error || 'Plan de paiement introuvable'}</span>
        </div>
      </div>
    );
  }

  const paidCount = paymentPlan.installments.filter((i) => i.status === 'PAID').length;
  const paidAmount = paymentPlan.installments.filter((i) => i.status === 'PAID').reduce((sum, i) => sum + i.amount, 0);

  // Calcul de la progression en pourcentage
  let progressPercent = 0;
  if (paymentPlan.paymentMode === 'FULL') {
    progressPercent = paymentPlan.status === 'COMPLETED' ? 100 : 0;
  } else {
    progressPercent = (paidAmount / paymentPlan.totalAmount) * 100;
  }

  const isFullyPaid = progressPercent === 100;

  const statusConfig = {
    PENDING_CHOICE: { label: 'En attente de choix', color: 'text-amber-600', bg: 'bg-amber-50' },
    PENDING_PAYMENT: { label: 'Paiement en attente', color: 'text-amber-600', bg: 'bg-amber-50' },
    IN_PROGRESS: { label: 'En cours', color: 'text-blue-600', bg: 'bg-blue-50' },
    OVERDUE: { label: 'En retard', color: 'text-red-600', bg: 'bg-red-50' },
    COMPLETED: { label: 'Terminé', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  }[paymentPlan.status] || { label: paymentPlan.status, color: 'text-slate-600', bg: 'bg-slate-50' };

  return (
    <div className="flex flex-col gap-6 max-w-[1400px] mx-auto w-full pb-10">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/payment-plans')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          aria-label="Retour"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Plan de Paiement</h1>
          <p className="text-slate-500 mt-1">{paymentPlan.booking?.reference || 'N/A'}</p>
        </div>

        <div className={`px-4 py-2 rounded-lg ${statusConfig.bg}`}>
          <span className={`text-sm font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
        </div>
      </div>

      {/* Info Client */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-5 h-5 text-slate-600" />
            <h3 className="text-lg font-bold text-slate-900">Informations Client</h3>
          </div>
          <p className="text-sm text-slate-500">
            Détails du client et de la réservation associée à ce plan de paiement.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div>
            <p className="text-xs text-slate-500 mb-1">Nom</p>
            <p className="text-sm font-medium text-slate-900">{paymentPlan.booking?.customerName || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">Email</p>
            <p className="text-sm font-medium text-slate-900">{paymentPlan.booking?.customerEmail || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">Espace</p>
            <p className="text-sm font-medium text-slate-900">
              {extractSpaceNameFromPriceBreakdown(paymentPlan.booking?.metadata) ||
                paymentPlan.booking?.spaceType?.name ||
                'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">Mode de paiement</p>
            <p className="text-sm font-medium text-slate-900">
              {paymentPlan.paymentMode === 'FULL' ? 'Paiement total' : `Mensuel (${paymentPlan.periodMonths} mois)`}
            </p>
          </div>
        </div>
      </div>

      {/* Progression */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        {isFullyPaid ? (
          /* UI de félicitations pour paiement complet */
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Paiement intégral reçu !</h3>
            <p className="text-slate-600 mb-6">
              Toutes les échéances ont été payées. Ce plan de paiement est officiellement terminé.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-6">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="text-2xl font-bold text-slate-900 mb-1">{paidCount}</div>
                <div className="text-sm text-slate-600">Échéances payées</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="text-2xl font-bold text-slate-900 mb-1">
                  {paymentPlan.totalAmount.toLocaleString('fr-FR')} FCFA
                </div>
                <div className="text-sm text-slate-600">Montant total</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="text-2xl font-bold text-emerald-600">100%</div>
                <div className="text-sm text-slate-600">Progression</div>
              </div>
            </div>

            <div className="w-full max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-700">Progression des paiements</span>
                <span className="text-sm font-bold text-emerald-600">100%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-full w-full transition-all" />
              </div>
            </div>
          </div>
        ) : (
          /* UI normale de progression */
          <>
            <div className="mb-2">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-5 h-5 text-slate-600" />
                <h3 className="text-lg font-bold text-slate-900">Progression des Paiements</h3>
              </div>
              <p className="text-sm text-slate-500">
                Suivi en temps réel de l'avancement des paiements et du montant total réglé par le client.
              </p>
            </div>

            <div className="space-y-4 mt-6">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Montant payé</span>
                <span className="font-bold text-emerald-600">{paidAmount.toLocaleString('fr-FR')} FCFA</span>
              </div>

              <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-full transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Progression</span>
                <span className="font-bold text-slate-900">{Math.round(progressPercent)}%</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Timeline des échéances */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-1">Échéances</h3>
          <p className="text-sm text-slate-500">
            Calendrier détaillé des mensualités avec leur statut de paiement. Vous pouvez valider manuellement les
            paiements reçus en cliquant sur le bouton "Valider" pour chaque échéance en attente.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3.5 text-left font-semibold text-xs uppercase tracking-wider">Mois</th>
                <th className="px-6 py-3.5 text-left font-semibold text-xs uppercase tracking-wider">
                  Date d'échéance
                </th>
                <th className="px-6 py-3.5 text-left font-semibold text-xs uppercase tracking-wider">Montant</th>
                <th className="px-6 py-3.5 text-left font-semibold text-xs uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3.5 text-left font-semibold text-xs uppercase tracking-wider">
                  Date de paiement
                </th>
                <th className="px-6 py-3.5 text-right font-semibold text-xs uppercase tracking-wider pr-6">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paymentPlan.installments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-slate-500 italic">
                    Aucune échéance pour ce mode de paiement (Paiement intégral).
                  </td>
                </tr>
              ) : (
                paymentPlan.installments
                  .sort((a, b) => a.monthNumber - b.monthNumber)
                  .map((installment) => {
                    const isPaid = installment.status === 'PAID';
                    const isOverdue = installment.status === 'OVERDUE';
                    const isPending = installment.status === 'PENDING';

                    return (
                      <tr
                        key={installment.id}
                        className={`transition-colors ${
                          isPaid
                            ? 'bg-emerald-50/30 hover:bg-emerald-50/50'
                            : isOverdue
                              ? 'bg-fuchsia-50/30 hover:bg-fuchsia-50/50'
                              : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="px-6 py-4">
                          <span className="font-semibold text-slate-900">Mois {installment.monthNumber}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-slate-700">
                            {new Date(installment.dueDate).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-bold text-slate-900">
                            {installment.amount.toLocaleString('fr-FR')} FCFA
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                              isPaid
                                ? 'bg-emerald-100 text-emerald-700'
                                : isOverdue
                                  ? 'bg-fuchsia-100 text-fuchsia-700'
                                  : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {isPaid ? (
                              <>
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Payé
                              </>
                            ) : isOverdue ? (
                              <>
                                <AlertCircle className="w-3.5 h-3.5" />
                                En retard
                              </>
                            ) : (
                              <>
                                <Clock className="w-3.5 h-3.5" />
                                En attente
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {installment.paidAt ? (
                            <span className="text-emerald-600 font-medium text-sm">
                              {new Date(installment.paidAt).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right pr-6">
                          {isPending && (
                            <button
                              onClick={() => handleOpenValidationModal(installment)}
                              disabled={validating === installment.id}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                            >
                              {validating === installment.id ? 'Validation...' : 'Valider'}
                            </button>
                          )}
                          {isOverdue && (
                            <button
                              onClick={() => handleOpenValidationModal(installment)}
                              disabled={validating === installment.id}
                              className="px-3 py-1.5 bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                            >
                              {validating === installment.id ? 'Validation...' : 'Valider'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de validation de paiement */}
      <MarkAsPaidModal
        open={showMarkAsPaidModal}
        onClose={() => {
          setShowMarkAsPaidModal(false);
          setSelectedInstallment(null);
        }}
        onConfirm={handleValidateInstallment}
        bookingReference={`${paymentPlan.booking?.reference || 'N/A'} - Mois ${selectedInstallment?.monthNumber || ''}`}
        totalAmount={selectedInstallment?.amount || 0}
      />
    </div>
  );
}
